import * as React from 'react';
import { Context as ReactContext } from 'react';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { Context } from '@micro-frontend-react/core/lib/Context';
import { useDynamicReducer } from '@micro-frontend-react/employee-experience/lib/useDynamicReducer';

import type { IPBTCase } from '../../../Interfaces/IPBTCase';
import {
  PBTCaseStatus,
  PBTEngagementModel,
  PBTFlag,
  isHrcGerWitReviewed,
  isHrcLoaReviewed,
  mapApiStatusToUiStatus,
} from '../../../Interfaces/IPBTCase';
import { PBTApiEndpoints } from '../../../Services/PBTConstants';
import { pbtCasesReducerName, pbtCasesReducer } from '../../../Redux/PBT/Cases/reducers';
import { requestCasesSagas } from '../../../Services/PBTCaseService';

/** Maps API status enum string to UI display string (shared, see IPBTCase). */

/** Maps API flag value to UI flag string array.
 * API may return: integer bit-field (0-7), or enum string ("None", "GerWit", "GerWit, Loa") */
function mapFlags(flags: string | number | undefined | null): IPBTCase['flags'] {
  if (flags === null || flags === undefined) return [] as unknown as IPBTCase['flags'];

  const result: string[] = [];

  if (typeof flags === 'number') {
    if (flags & 1) result.push(PBTFlag.GERWIT);
    if (flags & 2) result.push(PBTFlag.LOA);
    if (flags & 4) result.push(PBTFlag.Overdue);
    if (flags & 8) result.push(PBTFlag.CrmMismatch);
  } else {
    // JsonStringEnumConverter sends comma-separated enum names
    const tokens = flags
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    if (tokens.includes('gerwit') || tokens.includes('ger')) result.push(PBTFlag.GERWIT);
    if (tokens.includes('loa')) result.push(PBTFlag.LOA);
    if (tokens.includes('overdue')) result.push(PBTFlag.Overdue);
    if (tokens.includes('crmmismatch')) result.push(PBTFlag.CrmMismatch);
  }

  return result as IPBTCase['flags'];
}

/** Maps case status to workflow stepper step number */
const statusToStep: Record<string, number> = {
  Pending: 1, // CRM Sync complete, entering details
  'Data Gathering': 3, // Data Gathering
  'Date & Attestation Pending': 4, // Manager Attestation (attestation phase — combined step)
  'Awaiting HRC Approval': 5, // HRC Validation Gate
  Complete: 6, // Offboarding
  Paused: 0,
  Cancelled: 0,
};

/** Maps API PBTCase entity to UI IPBTCase shape */
export function mapApiCaseToUiCase(apiCase: Record<string, unknown>): IPBTCase {
  const uiStatus = mapApiStatusToUiStatus(apiCase.status as string) as IPBTCase['status'];
  const hasCompletedManagerAttestation =
    uiStatus === PBTCaseStatus.AwaitingHrcApproval || uiStatus === PBTCaseStatus.Complete;
  const rawEngModel = (apiCase.engagementModel as string) || '';
  // Normalize engagement model to match dropdown keys (case-insensitive match).
  // Keep an unset value blank instead of defaulting to 'Manager-led', which would
  // mask a genuinely unset engagement model (Bug 16433853).
  const engModelValues = Object.values(PBTEngagementModel) as string[];
  const matchedEngModel = rawEngModel
    ? engModelValues.find((v) => v.toLowerCase() === rawEngModel.toLowerCase()) ?? rawEngModel
    : '';

  const workState = (apiCase.workState as string) || undefined;
  const personalEmail = (apiCase.personalEmail as string) || undefined;
  const homeAddr = (apiCase.homeAddress as string) || (apiCase.homeAddr as string) || undefined;

  // Data Status is computed server-side from live HRDP/MIMS field completeness (the same evaluation
  // the Employee Data tab and the workflow gate use). Consume it directly rather than re-deriving on
  // the client from the lightweight list payload — which lacks the employee-data fields and therefore
  // always defaulted to "Complete", masking blocked cases on the dashboard (bug 16434325).
  // Fail closed: normalize a missing value to 'Blocked' so the dashboard column, the data-status
  // filter, and the CaseDetail workflow gate (dataStatus === 'Blocked') all agree on the same case.
  const dataStatus = (apiCase.dataStatus as IPBTCase['dataStatus']) || 'Blocked';

  // Termination-email delivery failure is derived server-side from TermEmailAudits (P0-3 / US 15949981):
  // the case row can read "Complete"/"Term Email Sent" because status is set at enqueue, while the async
  // EmailProcessor later failed to deliver. Surface it as the existing "Email Failed" flag so it shows on
  // the dashboard Flags column and the FlagBadge without any new UI, alerting the HRC that follow-up is needed.
  const emailDeliveryFailed = apiCase.termEmailDeliveryFailed === true;
  const flags = mapFlags(apiCase.flags as string | number);
  if (emailDeliveryFailed && !flags.includes(PBTFlag.EmailFailed)) {
    flags.push(PBTFlag.EmailFailed);
  }

  return {
    id: apiCase.caseId as number,
    empName: (apiCase.employeeName as string) || '',
    empAlias: (apiCase.employeeAlias as string) || '',
    pern: (apiCase.personnelNumber as string) || '',
    crmCase: (apiCase.crmCaseId as string) || '',
    status: uiStatus,
    engModel: matchedEngModel as IPBTCase['engModel'],
    manager: (apiCase.managerName as string) || '',
    managerAlias: (apiCase.managerAlias as string) || undefined,
    hrc: (apiCase.hrcAlias as string) || '',
    backupHrc: (apiCase.backupHrcAlias as string) || null,
    termConvDate: (apiCase.termConversationDate as string) || null,
    effTermDate: hasCompletedManagerAttestation
      ? (apiCase.effectiveTerminationDate as string) || null
      : null,
    termEmailSentDate: (apiCase.termEmailSentDate as string) || null,
    termEmailSent: apiCase.termEmailSent === true,
    daysInStatus: (apiCase.daysInStatus as number) || 0,
    flags,
    emailFailed: emailDeliveryFailed,
    hrcLoaReviewed: isHrcLoaReviewed(apiCase.flags),
    hrcGerWitReviewed: isHrcGerWitReviewed(apiCase.flags),
    gerWitCheckStatus:
      (apiCase.gerwitCheckStatus as string)
      || (apiCase.gerWitCheckStatus as string)
      || (apiCase.GERWITCheckStatus as string)
      || null,
    outcome: (apiCase.crmOutcome as string) || null,
    rehire: (apiCase.rehireEligibility as string) || null,
    currentStep: statusToStep[uiStatus] || 1,
    dataStatus,
    workState,
    homeState: (apiCase.homeState as string) || undefined,
    personalEmail,
    immigration: (apiCase.immigration as string) || undefined,
    immigrationStatus: (apiCase.immigrationStatus as IPBTCase['immigrationStatus']) || undefined,
    visaType: (apiCase.visaType as string) || undefined,
    workAddr: (apiCase.workAddress as string) || (apiCase.workAddr as string) || undefined,
    homeAddr,
    sevenDay: apiCase.sevenDay as boolean | undefined,
    kickoffEmailSent: apiCase.kickoffEmailSent === true,
    kickoffEmailSentAt: (apiCase.kickoffEmailSentAt as string) || null,
    reminderCount: (apiCase.reminderCount as number) || 0,
    manualReminderCount: (apiCase.manualReminderCount as number) || 0,
    lastReminderSentAt: (apiCase.lastReminderSentAt as string) || null,
    managerNonResponsive: (apiCase.managerNonResponsive as boolean) || false,
    hrcName: (apiCase.hrcName as string) || undefined,
    geoRestricted: apiCase.isManagerGeoRestricted === true,
    hrcValidationReleasedBy: (apiCase.hrcValidationReleasedBy as string) || null,
    isManagerChanged: apiCase.isManagerChanged === true,
    isManagerChangeReviewed: apiCase.isManagerChangeReviewed === true,
  };
}

// ── Module-level fetch state (survives any component mount/unmount) ──
type FetchStatus = 'idle' | 'loading' | 'done' | 'error';
let _status: FetchStatus = 'idle';
let _cases: IPBTCase[] = [];
let _error: string | null = null;
const _listeners = new Set<() => void>();
let _requestSequence = 0;
let _listRequestSequence = 0;
const _caseRefreshSequence = new Map<number, number>();
let _authClient: { acquireToken(scopes: string[]): Promise<string | null> } | null = null;
let _pageNumber = 1;
let _pageSize = 10;
let _totalCount = 0;
let _totalPages = 0;
let _stats = {
  totalActive: 0,
  dataGathering: 0,
  awaiting: 0,
  complete: 0,
  flagged: 0,
};

// All per-column filters live here so every filter is applied server-side across the full result
// set (not just the loaded page). Setters mutate this and trigger a fresh server fetch.
type CasesFilters = {
  search: string;
  statCard: string; // '' | 'totalActive' | 'dataGathering' | 'awaiting' | 'complete'
  statusFilter: string; // UI display status (PBTCaseStatus value) or ''
  dataStatus: string;
  engModel: string;
  crmCase: string;
  manager: string;
  termConvDate: string;
  effTermDate: string;
  flaggedOnly: boolean;
};
const emptyFilters: CasesFilters = {
  search: '',
  statCard: '',
  statusFilter: '',
  dataStatus: '',
  engModel: '',
  crmCase: '',
  manager: '',
  termConvDate: '',
  effTermDate: '',
  flaggedOnly: false,
};
let _filters: CasesFilters = { ...emptyFilters };
type CasesFilterOptions = {
  managers: string[];
  crmCases: string[];
  termConvDates: string[];
  effTermDates: string[];
};
let _filterOptions: CasesFilterOptions = {
  managers: [],
  crmCases: [],
  termConvDates: [],
  effTermDates: [],
};

function notify() {
  _listeners.forEach((fn) => fn());
}

async function acquireCasesToken(authClient: {
  acquireToken(scopes: string[]): Promise<string | null>;
}): Promise<string> {
  let firstError: unknown;

  try {
    const token = await authClient.acquireToken(__SCOPES__);
    if (token) return token;
  } catch (err) {
    // Authentication can still be initializing when the dashboard first mounts.
    firstError = err;
  }

  await new Promise((resolve) => setTimeout(resolve, 750));
  const retryToken = await authClient.acquireToken(__SCOPES__);
  if (!retryToken) {
    if (firstError instanceof Error) throw firstError;
    throw new Error('Unable to acquire an access token for PBT cases.');
  }
  return retryToken;
}

/** Update a single case in the module-level cache and notify subscribers */
export function updateCaseInStore(caseId: number, updates: Partial<IPBTCase>) {
  _cases = _cases.map((c) => (c.id === caseId ? { ...c, ...updates } : c));
  notify();
}

const CASES_PAGE_SIZE = 10;

/** Maps a UI display status (e.g. "Data Gathering") to the API PbtCaseStatus enum name
 * (e.g. "DataGathering") expected by the `status` query param. Returns '' when unmapped. */
function uiStatusToApiName(display: string): string {
  const entry = Object.entries(PBTCaseStatus).find(([, value]) => value === display);
  return entry ? entry[0] : '';
}

/** Translates the current filter state into the flat query params the API expects. */
function buildRequestParams(pageNumber: number, includeSummary: boolean) {
  const f = _filters;
  let status = '';
  let activeOnly = false;
  if (f.statCard) {
    if (f.statCard === 'totalActive') activeOnly = true;
    else if (f.statCard === 'dataGathering') status = 'DataGathering';
    else if (f.statCard === 'awaiting') status = 'AwaitingAttestation';
    else if (f.statCard === 'complete') status = 'Complete';
  } else if (f.statusFilter) {
    status = uiStatusToApiName(f.statusFilter);
  }
  return {
    pageNumber,
    pageSize: CASES_PAGE_SIZE,
    search: f.search,
    status,
    activeOnly,
    crmCase: f.crmCase,
    manager: f.manager,
    engagementModel: f.engModel,
    dataStatus: f.dataStatus,
    termConvDate: f.termConvDate,
    effTermDate: f.effTermDate,
    flaggedOnly: f.flaggedOnly,
    includeSummary,
  };
}

function buildCasesPageUrl(pageNumber: number, includeSummary: boolean): string {
  return PBTApiEndpoints.CasesPage(buildRequestParams(pageNumber, includeSummary));
}


function parseCasesEnvelope(json: unknown): {
  raw: Record<string, unknown>[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  stats: typeof _stats | null;
  filterOptions: CasesFilterOptions | null;
} {
  const root = json as Record<string, unknown> | null | undefined;
  const envelope = (root?.data ?? root?.Data) as Record<string, unknown> | undefined;
  const raw = ((envelope?.cases ?? envelope?.Cases) ?? []) as Record<string, unknown>[];
  const summary = (envelope?.summary ?? envelope?.Summary) as Record<string, unknown> | undefined;
  const optionsRaw = (envelope?.filterOptions ?? envelope?.FilterOptions) as Record<string, unknown> | undefined;
  const pageNumberValue = Number(envelope?.pageNumber ?? envelope?.PageNumber);
  const pageSizeValue = Number(envelope?.pageSize ?? envelope?.PageSize);
  const totalCountValue = Number(envelope?.totalCount ?? envelope?.TotalCount);
  const totalPagesValue = Number(envelope?.totalPages ?? envelope?.TotalPages);
  const totalPages = Number.isFinite(totalPagesValue) && totalPagesValue > 0 ? totalPagesValue : 1;
  const numberOr = (camelName: string, pascalName: string, fallback: number) => {
    const value = Number(summary?.[camelName] ?? summary?.[pascalName]);
    return Number.isFinite(value) ? value : fallback;
  };
  // Only trust server-computed stats — they cover the full result set. When the server omits the
  // summary (cheap page-navigation fetch, includeSummary=false) return null so the caller keeps the
  // previously loaded stats rather than recomputing from the current 10-row page (which is wrong).
  const stats = summary
    ? {
        totalActive: numberOr('totalActive', 'TotalActive', 0),
        dataGathering: numberOr('dataGathering', 'DataGathering', 0),
        awaiting: numberOr('awaitingAttestation', 'AwaitingAttestation', 0),
        complete: numberOr('complete', 'Complete', 0),
        flagged: numberOr('flagged', 'Flagged', 0),
      }
    : null;
  const strArray = (camelName: string, pascalName: string): string[] => {
    const value = (optionsRaw?.[camelName] ?? optionsRaw?.[pascalName]) as unknown;
    return Array.isArray(value) ? value.map((v) => String(v)) : [];
  };
  const filterOptions = optionsRaw
    ? {
        managers: strArray('managers', 'Managers'),
        crmCases: strArray('crmCases', 'CrmCases'),
        termConvDates: strArray('termConvDates', 'TermConvDates'),
        effTermDates: strArray('effTermDates', 'EffTermDates'),
      }
    : null;
  return {
    raw,
    pageNumber: Number.isFinite(pageNumberValue) && pageNumberValue > 0 ? pageNumberValue : 1,
    pageSize: Number.isFinite(pageSizeValue) && pageSizeValue > 0 ? pageSizeValue : CASES_PAGE_SIZE,
    totalCount: Number.isFinite(totalCountValue) && totalCountValue >= 0 ? totalCountValue : raw.length,
    totalPages,
    stats,
    filterOptions,
  };
}

async function fetchCasesPage(
  token: string,
  pageNumber: number,
  includeSummary: boolean,
): Promise<ReturnType<typeof parseCasesEnvelope>> {
  const res = await fetch(buildCasesPageUrl(pageNumber, includeSummary), {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`API returned ${res.status}`);
  return parseCasesEnvelope(await res.json());
}

type StartFetchOptions = {
  includeSummary?: boolean;
  forceRefresh?: boolean;
  preserveCasesOnError?: boolean;
  clearCases?: boolean;
};

function startFetch(
  authClient: { acquireToken(scopes: string[]): Promise<string | null> },
  pageNumber = _pageNumber,
  options: StartFetchOptions = {},
) {
  const {
    includeSummary = false,
    forceRefresh = false,
    preserveCasesOnError = false,
    clearCases = false,
  } = options;
  if (_authClient !== authClient) {
    _authClient = authClient;
    _status = 'idle';
    _cases = [];
    _error = null;
    _pageNumber = 1;
    _filters = { ...emptyFilters };
  }
  if (_status === 'done' && !forceRefresh && _pageNumber === pageNumber) {
    return;
  }

  const listRequestSequence = ++_listRequestSequence;
  const requestSequence = ++_requestSequence;
  _pageNumber = pageNumber;
  _status = 'loading';
  // Stale-page fix: on a user-initiated page/filter/search change, drop the previous page's rows
  // immediately so the grid shows a loading state instead of the old records for 2-3 seconds. A
  // background refresh (clearCases=false) keeps the current rows visible while it revalidates.
  if (clearCases) {
    _cases = [];
  }
  notify();

  acquireCasesToken(authClient)
    .then((token) => fetchCasesPage(token, pageNumber, includeSummary))
    .then((envelope) => {
      if (listRequestSequence !== _listRequestSequence) return;
      const previousCases = new Map(_cases.map((item) => [item.id, item]));
      const fetchedCases = envelope.raw
        .map(mapApiCaseToUiCase)
        .filter((c) => c.status !== PBTCaseStatus.Removed);
      _cases = fetchedCases
        .map((item) => {
          const refreshedAfterListStarted = (_caseRefreshSequence.get(item.id) ?? 0) > requestSequence;
          return refreshedAfterListStarted ? previousCases.get(item.id) ?? null : item;
        })
        .filter((item): item is IPBTCase => item !== null);
      _pageNumber = envelope.pageNumber;
      _pageSize = envelope.pageSize;
      _totalCount = envelope.totalCount;
      _totalPages = envelope.totalPages;
      if (envelope.stats) _stats = envelope.stats;
      if (envelope.filterOptions) _filterOptions = envelope.filterOptions;
      _status = 'done';
      _error = null;
      notify();
    })
    .catch((err) => {
      if (listRequestSequence !== _listRequestSequence) return;
      _error = (err as Error).message;
      if (!preserveCasesOnError) {
        _cases = [];
      }
      _status = 'error';
      notify();
    });
}

/** Subscribe to module-level fetch state changes (React 17 compatible) */
function useCasesStore() {
  const [, setTick] = React.useState(0);
  const tickRef = React.useRef<() => void>(() => {
    /* placeholder */
  });
  tickRef.current = () => setTick((n) => n + 1);

  React.useEffect(() => {
    const listener = () => tickRef.current();
    _listeners.add(listener);
    // If data already arrived before we subscribed, force one re-render
    if (_status === 'done' || _status === 'error') {
      listener();
    }
    return () => {
      _listeners.delete(listener);
    };
  }, []);

  return {
    cases: _cases,
    isLoading: _status === 'loading' || _status === 'idle',
    error: _error,
    pageNumber: _pageNumber,
    pageSize: _pageSize,
    totalCount: _totalCount,
    totalPages: _totalPages,
    searchText: _filters.search,
    stats: _stats,
    filters: _filters,
    filterOptions: _filterOptions,
  };
}

export function useHRCDashboard() {
  const { authClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);

  // Register the PBT cases reducer
  useDynamicReducer(pbtCasesReducerName, pbtCasesReducer, [requestCasesSagas]);

  // Initial load: fetch page 1 with the summary + filter options. Navigation and filter changes
  // request their own page (options/summary are reused unless a full refresh is triggered).
  React.useEffect(() => {
    // Reset the module-level filters/page on dashboard mount so navigating away and back with the same
    // authClient doesn't reuse the prior page's filters (which would leave the grid filtered while the
    // local dropdown inputs render blank), and so we always issue a request even when we were already
    // on page 1 (where startFetch would otherwise early-return).
    _filters = { ...emptyFilters };
    _pageNumber = 1;
    startFetch(authClient, 1, { includeSummary: true, forceRefresh: true, clearCases: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshCases = React.useCallback(() => {
    // Background revalidation: refresh the current page plus stats/options without blanking the grid.
    startFetch(authClient, _pageNumber, {
      forceRefresh: true,
      preserveCasesOnError: true,
      includeSummary: true,
    });
  }, [authClient]);

  const {
    cases: allCases,
    isLoading,
    error,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    searchText,
    stats,
    filters,
    filterOptions,
  } = useCasesStore();

  const casesState = {
    cases: allCases,
    isLoading,
    hasError: !!error,
    errorMessage: error,
  };

  // All filters are server-side: each setter updates the shared filter state, resets to page 1,
  // and re-fetches (clearing the current rows so the grid never shows a stale page). Column filters
  // keep the cheaper list-only path. For includeSummary requests, the API applies these filters only
  // to Cases/TotalCount and computes Summary/FilterOptions independently from the caller's base scope,
  // so stat-card changes can refresh the global cards/banner in the same request without narrowing them.
  const applyFilterChange = React.useCallback(
    (mutate: (draft: CasesFilters) => void, options: { includeSummary?: boolean } = {}) => {
      _filters = { ..._filters };
      mutate(_filters);
      startFetch(authClient, 1, {
        forceRefresh: true,
        clearCases: true,
        includeSummary: options.includeSummary ?? false,
      });
    },
    [authClient]
  );

  const statusFilter = filters.statusFilter;
  const engModelFilter = filters.engModel;
  const dataStatusFilter = filters.dataStatus;
  const showFlaggedOnly = filters.flaggedOnly;
  const activeStatCard = filters.statCard || null;
  const [selectedCase, setSelectedCase] = React.useState<IPBTCase | null>(null);

  const setStatusFilter = React.useCallback(
    (value: string) => applyFilterChange((f) => {
      f.statusFilter = value;
      if (value) f.statCard = '';
    }),
    [applyFilterChange]
  );
  const setEngModelFilter = React.useCallback(
    (value: string) => applyFilterChange((f) => { f.engModel = value; }),
    [applyFilterChange]
  );
  const setDataStatusFilter = React.useCallback(
    (value: string) => applyFilterChange((f) => { f.dataStatus = value; }),
    [applyFilterChange]
  );
  const setShowFlaggedOnly = React.useCallback(
    (value: boolean) => applyFilterChange((f) => { f.flaggedOnly = value; }),
    [applyFilterChange]
  );
  const setManagerFilter = React.useCallback(
    (value: string) => applyFilterChange((f) => { f.manager = value; }),
    [applyFilterChange]
  );
  const setCrmCaseFilter = React.useCallback(
    (value: string) => applyFilterChange((f) => { f.crmCase = value; }),
    [applyFilterChange]
  );
  const setTermConvDateFilter = React.useCallback(
    (value: string) => applyFilterChange((f) => { f.termConvDate = value; }),
    [applyFilterChange]
  );
  const setEffTermDateFilter = React.useCallback(
    (value: string) => applyFilterChange((f) => { f.effTermDate = value; }),
    [applyFilterChange]
  );

  const refreshCase = React.useCallback(
    async (caseId: number, signal?: AbortSignal): Promise<IPBTCase | null> => {
      const token = await acquireCasesToken(authClient);
      const response = await fetch(PBTApiEndpoints.CaseDetail(caseId), {
        method: 'GET',
        headers: { Authorization: ['Bearer', token].join(' '), 'Content-Type': 'application/json' },
        signal,
      });
      if (!response.ok) throw new Error(`API returned ${response.status}`);

      const json = await response.json();
      const freshCase = mapApiCaseToUiCase((json?.data ?? json?.Data ?? json) as Record<string, unknown>);
      _caseRefreshSequence.set(caseId, ++_requestSequence);
      _cases =
        freshCase.status === PBTCaseStatus.Removed
          ? _cases.filter((item) => item.id !== caseId)
          : _cases.map((item) => (item.id === caseId ? freshCase : item));
      notify();
      setSelectedCase((current) =>
        current?.id === caseId ? (freshCase.status === PBTCaseStatus.Removed ? null : freshCase) : current
      );
      return freshCase.status === PBTCaseStatus.Removed ? null : freshCase;
    },
    [authClient]
  );

  const setSearchText = React.useCallback(
    (value: string) => {
      const normalized = value.trim().length >= 3 ? value.trim() : '';
      if (_pageNumber === 1 && _filters.search === normalized) return;
      _filters = { ..._filters, search: normalized };
      startFetch(authClient, 1, { forceRefresh: true, clearCases: true, includeSummary: false });
    },
    [authClient]
  );

  const setPageNumber = React.useCallback(
    (value: number) => {
      if (_pageNumber === value) return;
      // Pure page navigation: skip the summary/options queries for a faster page switch, and clear the
      // current rows so the previous page's records don't linger while the next page loads.
      startFetch(authClient, value, { forceRefresh: true, clearCases: true, includeSummary: false });
    },
    [authClient]
  );

  // All filtering is server-side now; the loaded page IS the filtered result set. No client filtering.
  const filteredCases = allCases;

  const handleStatCardClick = React.useCallback(
    (key: string) => {
      applyFilterChange((f) => {
        const next = f.statCard === key ? '' : key;
        f.statCard = next;
        // A stat card and the status dropdown both drive the server status filter — clear the dropdown
        // (and engagement model, matching the prior UX) when a card takes over.
        if (next) {
          f.statusFilter = '';
          f.engModel = '';
        }
      }, { includeSummary: true });
    },
    [applyFilterChange]
  );

  const handleRowClick = React.useCallback((item: IPBTCase) => {
    setSelectedCase((prev) => (prev?.id === item.id ? null : item));
  }, []);

  const flaggedCount = stats.flagged;

  /** Update a case in-place in the module-level list after a successful PUT */
  const updateCaseInList = React.useCallback((updatedCase: IPBTCase) => {
    const idx = _cases.findIndex((c) => c.id === updatedCase.id);
    if (idx >= 0) {
      _cases[idx] = updatedCase;
      notify();
    }
    setSelectedCase(updatedCase);
  }, []);

  return {
    casesState,
    allCases,
    filteredCases,
    stats,
    flaggedCount,
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,
    engModelFilter,
    setEngModelFilter,
    dataStatusFilter,
    setDataStatusFilter,
    showFlaggedOnly,
    setShowFlaggedOnly,
    managerFilter: filters.manager,
    setManagerFilter,
    crmCaseFilter: filters.crmCase,
    setCrmCaseFilter,
    termConvDateFilter: filters.termConvDate,
    setTermConvDateFilter,
    effTermDateFilter: filters.effTermDate,
    setEffTermDateFilter,
    filterOptions,
    activeStatCard,
    handleStatCardClick,
    handleRowClick,
    selectedCase,
    setSelectedCase,
    updateCaseInList,
    refreshCases,
    refreshCase,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    setPageNumber,
  };
}
